import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Button, Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { zIndexLevel } from "src/utils/zIndexLevels";
import { useTour } from "./store/hooks";
import { CARD_WIDTH, cardPositionOf, Rect, sameRect } from "./store/position";
import { TourStep } from "./store/types";

const POLL_MS = 120;
/** How long a step waits for the screen to load before deciding it does not apply here. */
const LOADING_GRACE_MS = 2500;
const HOLE_PADDING = 6;
const DIM = "rgba(0, 0, 0, 0.45)";

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndexLevel.TOUR};
  pointer-events: none;
`;

const Dim = styled.div`
  position: fixed;
  background: ${DIM};
  pointer-events: auto;
`;

const Ring = styled.div<{ $isTask: boolean }>`
  position: fixed;
  border: 2px solid ${({ $isTask }) => ($isTask ? Color.yellow : Color.white)};
  border-radius: 5px;
  /* A step the user only reads about is kept out of reach; a task is theirs to click. */
  pointer-events: ${({ $isTask }) => ($isTask ? "none" : "auto")};
`;

const Card = styled.div`
  position: fixed;
  width: ${CARD_WIDTH}px;
  box-sizing: border-box;
  padding: 16px;
  background: ${Color.white};
  border-radius: 5px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
`;

const Progress = styled.div<{ $percent: number }>`
  height: 3px;
  border-radius: 2px;
  background: ${Color.lightGrayHover};

  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    border-radius: 2px;
    background: ${Color.primary};
  }
`;

const TaskBox = styled.div`
  padding: 8px 10px;
  border-left: 3px solid ${Color.yellow};
  background: ${Color.lightGray};
`;

const isOnScreen = (selector: string | undefined): boolean => {
  if (!selector) return false;
  const element = document.querySelector(selector);
  if (!element) return false;
  const { width, height } = element.getBoundingClientRect();
  return width > 0 && height > 0;
};

const rectOf = (selector: string | undefined): Rect | null => {
  if (!selector) return null;
  const element = document.querySelector(selector);
  if (!element) return null;
  const { top, left, width, height } = element.getBoundingClientRect();
  if (width === 0 || height === 0) return null;
  return { top, left, width, height };
};

const isTaskDone = (step: TourStep): boolean => {
  if (!step.task) return false;
  const { doneWhenPresent, doneWhenAbsent } = step.task;
  return (
    (doneWhenPresent !== undefined && isOnScreen(doneWhenPresent)) ||
    (doneWhenAbsent !== undefined && !isOnScreen(doneWhenAbsent))
  );
};

export const Tour = () => {
  const { state, step, next, back, end } = useTour();
  const { t, tDynamic } = useTranslation("common");
  const [rect, setRect] = useState<Rect | null>(null);
  const [isShown, setIsShown] = useState(false);
  const [cardHeight, setCardHeight] = useState(0);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const cardRef = useRef<HTMLDivElement>(null);
  // Since a step was last on screen: a run of steps that do not apply shares one wait.
  const lastShownAtRef = useRef(Date.now());
  const hasScrolledRef = useRef(false);
  const actionsRef = useRef({ next, back, end });
  actionsRef.current = { next, back, end };

  useEffect(() => {
    setIsShown(false);
    setRect(null);
    hasScrolledRef.current = false;
    if (!state || !step) return;
    // Going back onto a task that is already done shows it: it moves on once it is done again.
    let isDoneOnArrival = state.direction === -1 && isTaskDone(step);

    const pass = () => {
      if (state.direction === 1) actionsRef.current.next();
      else actionsRef.current.back();
    };

    const tick = () => {
      setViewport((current) =>
        current.width === window.innerWidth && current.height === window.innerHeight
          ? current
          : { width: window.innerWidth, height: window.innerHeight },
      );

      if (step.requires && !isOnScreen(step.requires)) {
        const isStillLoading =
          state.direction === 1 &&
          !isOnScreen(step.settledBy) &&
          Date.now() - lastShownAtRef.current < LOADING_GRACE_MS;
        if (!isStillLoading) pass();
        return;
      }

      const isDone = isTaskDone(step);
      if (!isDone) isDoneOnArrival = false;
      if (isDone && !isDoneOnArrival) {
        // A task done is a step forward, whichever way the user came onto it.
        actionsRef.current.next();
        return;
      }

      const target = step.target ? document.querySelector(step.target) : null;
      if (target && !hasScrolledRef.current) {
        hasScrolledRef.current = true;
        const bounds = target.getBoundingClientRect();
        if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
          target.scrollIntoView({ block: "center" });
        }
      }

      const nextRect = rectOf(step.target);
      setRect((current) => (sameRect(current, nextRect) ? current : nextRect));
      setIsShown(true);
      lastShownAtRef.current = Date.now();
    };

    const interval = window.setInterval(tick, POLL_MS);
    tick();
    return () => window.clearInterval(interval);
  }, [state?.stepIndex, state?.steps]);

  useEffect(() => {
    if (!state) return;
    // The overlay only stops the mouse and editor shortcuts listen on the window: only Tab,
    // Escape, and Enter/Space on the card or a task's target get through.
    const guardKeys = (event: KeyboardEvent) => {
      if (event.key === "Tab") return;
      const isActivation = event.key === "Enter" || event.key === " ";
      if (isActivation && event.target instanceof Node) {
        const taskTarget = step?.task && step.target ? document.querySelector(step.target) : null;
        if (cardRef.current?.contains(event.target) || taskTarget?.contains(event.target)) return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.type === "keydown" && event.key === "Escape") actionsRef.current.end();
    };
    window.addEventListener("keydown", guardKeys, true);
    window.addEventListener("keyup", guardKeys, true);
    return () => {
      window.removeEventListener("keydown", guardKeys, true);
      window.removeEventListener("keyup", guardKeys, true);
    };
  }, [state !== null, step]);

  useLayoutEffect(() => {
    const height = cardRef.current?.getBoundingClientRect().height ?? 0;
    if (height !== cardHeight) setCardHeight(height);
  });

  if (!state || !step) return null;

  // Waiting on a screen that is still loading: the dim stays, so the page
  // under it does not flash back into reach between two steps.
  if (!isShown) {
    return createPortal(
      <Layer data-testid="tour" data-tour-step={step.id} data-tour-waiting="" aria-busy>
        <Dim style={{ inset: 0 }} />
      </Layer>,
      document.body,
    );
  }

  const hole = rect && {
    top: Math.max(0, rect.top - HOLE_PADDING),
    left: Math.max(0, rect.left - HOLE_PADDING),
    width: rect.width + HOLE_PADDING * 2,
    height: rect.height + HOLE_PADDING * 2,
  };
  const position = cardPositionOf({
    target: hole,
    placement: step.placement ?? "bottom",
    cardHeight,
    viewport,
  });
  const isFirst = state.stepIndex === 0;
  const isLast = state.stepIndex === state.steps.length - 1;
  const percent = Math.round(((state.stepIndex + 1) / state.steps.length) * 100);

  return createPortal(
    <Layer data-testid="tour" data-tour-step={step.id}>
      {hole ? (
        <>
          <Dim style={{ top: 0, left: 0, right: 0, height: hole.top }} />
          <Dim style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }} />
          <Dim style={{ top: hole.top, left: 0, width: hole.left, height: hole.height }} />
          <Dim
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              right: 0,
              height: hole.height,
            }}
          />
          <Ring $isTask={step.task !== undefined} style={hole} data-testid="tour-target" />
        </>
      ) : (
        <Dim style={{ inset: 0 }} />
      )}

      <Card
        ref={cardRef}
        style={position}
        role="dialog"
        aria-label={tDynamic(step.title)}
        data-testid="tour-card"
      >
        <Flex column gap="10px">
          {state.steps.length > 1 && <Progress $percent={percent} />}

          <Text variant="h5" color={Color.primaryActive}>
            {tDynamic(step.title)}
          </Text>

          <Text variant="body3" color={Color.textSecondary}>
            {tDynamic(step.body)}
          </Text>

          {step.task && (
            <TaskBox data-testid="tour-task">
              <Flex column gap="2px">
                <Text variant="small1" bold>
                  {t("tour.card.yourTurn")}
                </Text>

                <Text variant="body3">{tDynamic(step.task.instruction)}</Text>
              </Flex>
            </TaskBox>
          )}

          <Flex justify="between" align="center" gap="10px">
            <Button name="tour-end" size="small" variant="borderless" onClick={end}>
              {t(isLast ? "tour.card.close" : "tour.card.end")}
            </Button>

            <Flex gap="8px">
              {!isFirst && !isLast && (
                <Button name="tour-back" size="small" variant="inverted" onClick={back}>
                  {t("tour.card.back")}
                </Button>
              )}

              {!isLast && (
                <Button
                  name="tour-next"
                  size="small"
                  variant={step.task ? "inverted" : "default"}
                  onClick={next}
                >
                  {t(
                    (step.task && "tour.card.skip") ||
                      (isFirst && "tour.card.start") ||
                      "tour.card.next",
                  )}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Layer>,
    document.body,
  );
};
